import { useEffect, useMemo, useRef, useState } from 'react';
import { TaskFormModal } from '../components/tasks/TaskFormModal';
import { TaskListRow } from '../components/tasks/TaskListRow';
import { TaskDetailModal } from '../components/dashboard/TaskDetailModal';
import { Modal } from '../components/ui/Modal';
import { PageHeader } from '../components/ui/PageHeader';
import { PageToolbar } from '../components/ui/PageToolbar';
import { useCrmData } from '../contexts/CrmDataContext';
import { taskAccountDisplay } from '../lib/taskAccountDisplay';
import { downloadTaskVisitListPdf } from '../lib/taskExport';
import { canCreatePersonalCrmRecords } from '../lib/roleAccess';
import { CRM_TASK_SAVE_SLICES } from '../lib/crmDataRefresh';
import { completeTaskAndMarkVisited } from '../lib/taskVisitationSync';
import type { Task } from '../types/crm';
import { clientById, userCanAccessClient, userCanWriteOwnedRecord } from '../utils/crmHelpers';
import { singaporeYmdKey } from '../utils/dashboardHelpers';
import { formatSingaporeDate, singaporeTodayDateString } from '../utils/dateTime';
import { taskDisplayStatus } from '../utils/taskHelpers';

type TaskSortKey = 'status' | 'title' | 'account' | 'notes' | 'owner' | 'newest';
type SortDir = 'asc' | 'desc';

type DayGroup = {
  ymd: string | null;
  label: string;
  tasks: Task[];
  dayStatus: 'Due' | 'Open' | null;
};

const SORT_COLUMNS: { key: Exclude<TaskSortKey, 'newest'>; label: string }[] = [
  { key: 'status', label: 'Status' },
  { key: 'title', label: 'Task' },
  { key: 'account', label: 'Account' },
  { key: 'notes', label: 'Notes' },
  { key: 'owner', label: 'Owner' },
];

const STATUS_ORDER: Record<string, number> = {
  Due: 0,
  'To Do': 1,
  'In Progress': 2,
  Done: 3,
};

const DEFAULT_DIR: Record<TaskSortKey, SortDir> = {
  status: 'asc',
  title: 'asc',
  account: 'asc',
  notes: 'asc',
  owner: 'asc',
  newest: 'desc',
};

function isTaskDone(task: Task): boolean {
  return taskDisplayStatus(task) === 'Done';
}

function taskRecencyKey(task: Task): string {
  return task.created_at || task.due_date || '';
}

function dayGroupStatus(dayTasks: Task[]): DayGroup['dayStatus'] {
  const open = dayTasks.filter((task) => !isTaskDone(task));
  if (open.length === 0) return null;
  if (open.some((task) => taskDisplayStatus(task) === 'Due')) return 'Due';
  return 'Open';
}

function dayGroupKey(group: DayGroup): string {
  return group.ymd ?? '__none__';
}

function compareTasks(
  a: Task,
  b: Task,
  sortKey: TaskSortKey,
  sortDir: SortDir,
  accountName: (t: Task) => string,
  ownerName: (ownerId?: string) => string,
): number {
  const aDone = isTaskDone(a) ? 1 : 0;
  const bDone = isTaskDone(b) ? 1 : 0;
  if (aDone !== bDone) return aDone - bDone;

  let cmp = 0;
  switch (sortKey) {
    case 'status':
      cmp = (STATUS_ORDER[taskDisplayStatus(a)] ?? 9) - (STATUS_ORDER[taskDisplayStatus(b)] ?? 9);
      break;
    case 'title':
      cmp = (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
      break;
    case 'account':
      cmp = accountName(a).localeCompare(accountName(b), undefined, { sensitivity: 'base' });
      break;
    case 'notes':
      cmp = (a.description ?? '').localeCompare(b.description ?? '', undefined, {
        sensitivity: 'base',
      });
      break;
    case 'owner':
      cmp = ownerName(a.owner_id).localeCompare(ownerName(b.owner_id), undefined, {
        sensitivity: 'base',
      });
      break;
    case 'newest':
    default:
      cmp = taskRecencyKey(a).localeCompare(taskRecencyKey(b));
      break;
  }

  if (cmp === 0) {
    return taskRecencyKey(b).localeCompare(taskRecencyKey(a));
  }
  return sortDir === 'asc' ? cmp : -cmp;
}

export function TasksPage() {
  const {
    data,
    getMyData,
    initialLoading,
    refresh,
    markTaskDoneLocal,
    ownerLabel,
    effectiveRole,
    username,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  } = useCrmData();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<TaskSortKey>('newest');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [completing, setCompleting] = useState(false);
  const today = singaporeTodayDateString();
  const [exportFrom, setExportFrom] = useState(today);
  const [exportTo, setExportTo] = useState(today);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tasksRefreshing, setTasksRefreshing] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set([today]));
  const expandedSeededRef = useRef(false);

  const scopeTasks = useMemo(() => [...getMyData('tasks')], [getMyData]);

  const tasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = scopeTasks;
    if (q) {
      list = list.filter((t) => {
        const client = clientById(data.clients, t.client_id);
        const account = taskAccountDisplay(t, client);
        const pool =
          `${t.title} ${t.status ?? ''} ${t.description ?? ''} ${t.prospect_company_name ?? ''} ${t.contact_person ?? ''} ${t.contact_phone ?? ''} ${t.address ?? ''} ${t.outlet_code ?? ''} ${t.outlet_name ?? ''} ${t.outlet_postal ?? ''} ${account.name} ${account.code} ${account.motherCode ?? ''} ${ownerLabel(t.owner_id)}`.toLowerCase();
        return pool.includes(q);
      });
    }

    const accountName = (t: Task) => {
      const client = clientById(data.clients, t.client_id);
      return taskAccountDisplay(t, client).name;
    };

    return [...list].sort((a, b) =>
      compareTasks(a, b, sortKey, sortDir, accountName, ownerLabel),
    );
  }, [scopeTasks, sortKey, sortDir, search, data.clients, ownerLabel]);

  const dayGroups = useMemo(() => {
    const map = new Map<string, Task[]>();
    const undated: Task[] = [];

    for (const task of tasks) {
      const key = singaporeYmdKey(task.due_date);
      if (!key) {
        undated.push(task);
        continue;
      }
      const list = map.get(key) ?? [];
      list.push(task);
      map.set(key, list);
    }

    const groups: DayGroup[] = [...map.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([ymd, dayTasks]) => ({
        ymd,
        label: formatSingaporeDate(`${ymd}T12:00:00+08:00`),
        tasks: dayTasks,
        dayStatus: dayGroupStatus(dayTasks),
      }));

    if (undated.length > 0) {
      groups.push({
        ymd: null,
        label: 'No due date',
        tasks: undated,
        dayStatus: dayGroupStatus(undated),
      });
    }

    return groups;
  }, [tasks]);

  useEffect(() => {
    if (expandedSeededRef.current || dayGroups.length === 0) return;
    const next = new Set<string>();
    if (dayGroups.some((g) => g.ymd === today)) next.add(today);
    for (const g of dayGroups) {
      if (g.dayStatus) next.add(dayGroupKey(g));
    }
    if (next.size === 0) next.add(dayGroupKey(dayGroups[0]!));
    setExpandedKeys(next);
    expandedSeededRef.current = true;
  }, [dayGroups, today]);

  function toggleSort(key: Exclude<TaskSortKey, 'newest'>) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(DEFAULT_DIR[key]);
  }

  function toggleGroup(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function canEditTask(task: Task): boolean {
    return userCanWriteOwnedRecord(task.owner_id, effectiveRole, username, data.users, supervisorTeam, {
      departmentSlug,
      clientId: task.client_id,
      clients: data.clients,
      actorSalespersonCodes,
    });
  }

  function openDetail(task: Task) {
    setSelectedTask(task);
  }

  function openEditFromDetail() {
    if (!selectedTask) return;
    setEditing(selectedTask);
    setSelectedTask(null);
    setEditOpen(true);
  }

  async function refreshTasksOnly() {
    setTasksRefreshing(true);
    try {
      await refresh({ silent: true, slices: CRM_TASK_SAVE_SLICES });
    } finally {
      setTasksRefreshing(false);
    }
  }

  async function handleDoneFromDetail() {
    if (!selectedTask) return;
    setCompleting(true);
    try {
      await completeTaskAndMarkVisited(selectedTask);
      markTaskDoneLocal(selectedTask.id);
      setSelectedTask(null);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not complete task.');
    } finally {
      setCompleting(false);
    }
  }

  async function handleExportPdf() {
    if (!exportFrom || !exportTo || exporting) return;
    setExporting(true);
    try {
      await downloadTaskVisitListPdf(scopeTasks, data.clients, data.contacts, {
        fromDate: exportFrom,
        toDate: exportTo,
      });
      setExportOpen(false);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not generate PDF.');
    } finally {
      setExporting(false);
    }
  }

  const canCreateTask = canCreatePersonalCrmRecords(effectiveRole);
  const detailClient = selectedTask ? clientById(data.clients, selectedTask.client_id) : undefined;
  const detailCanOpenAccount = userCanAccessClient(
    detailClient,
    effectiveRole,
    username,
    data.users,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  );
  const detailMinute = useMemo(() => {
    if (!selectedTask?.meeting_minute_id) return null;
    return data.meetingMinutes.find((m) => m.id === selectedTask.meeting_minute_id) ?? null;
  }, [data.meetingMinutes, selectedTask?.meeting_minute_id]);

  return (
    <div className="tasks-page">
      <PageHeader
        title="Tasks"
        description="Schedule and track follow-ups across your accounts."
        actions={
          canCreateTask ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => {
                setEditing(null);
                setEditOpen(true);
              }}
            >
              + New
            </button>
          ) : undefined
        }
      />

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by title, account code, contact, notes or owner"
        action={
          <div className="tasks-page__toolbar-aside">
            <span className="page-count">
              {initialLoading
                ? 'Loading…'
                : tasksRefreshing
                  ? 'Refreshing…'
                  : `${tasks.length} task${tasks.length === 1 ? '' : 's'} · ${dayGroups.length} day${
                    dayGroups.length === 1 ? '' : 's'
                  }`}
            </span>
            <div className="tasks-page__toolbar-controls">
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={exporting || initialLoading}
                onClick={() => setExportOpen(true)}
              >
                Export PDF
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => void refreshTasksOnly()}
                disabled={tasksRefreshing || initialLoading}
              >
                {tasksRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        }
      />

      {initialLoading && scopeTasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading tasks…</p>
        </div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <p>No tasks match your filters.</p>
        </div>
      ) : (
        <div className="task-list card">
          <div className="task-list__head" role="row">
            {SORT_COLUMNS.map(({ key, label }) => {
              const active = sortKey === key;
              const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <button
                  key={key}
                  type="button"
                  className={`task-list__sort-btn${active ? ' is-active' : ''}`}
                  onClick={() => toggleSort(key)}
                  aria-sort={ariaSort}
                >
                  <span>{label}</span>
                  {active ? (
                    <span className="task-list__sort-ind" aria-hidden>
                      {sortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <span />
          </div>

          <div className="task-list__days">
            {dayGroups.map((group) => {
              const key = dayGroupKey(group);
              const expanded = expandedKeys.has(key);
              const count = group.tasks.length;
              return (
                <section key={key} className={`task-list-day${expanded ? ' is-expanded' : ''}`}>
                  <button
                    type="button"
                    className="task-list-day__toggle"
                    aria-expanded={expanded}
                    onClick={() => toggleGroup(key)}
                  >
                    <span className="task-list-day__chevron" aria-hidden>
                      {expanded ? '▾' : '▸'}
                    </span>
                    <span className="task-list-day__date">{group.label}</span>
                    {group.ymd === today ? (
                      <span className="task-list-day__today">Today</span>
                    ) : null}
                    {group.dayStatus ? (
                      <span
                        className={`task-list-day__status task-status ${
                          group.dayStatus === 'Due' ? 'task-status--overdue' : 'task-status--todo'
                        }`}
                      >
                        {group.dayStatus}
                      </span>
                    ) : null}
                    <span className="task-list-day__count">
                      {count} task{count === 1 ? '' : 's'}
                    </span>
                  </button>

                  {expanded ? (
                    <div className="task-list-day__rows">
                      {group.tasks.map((t) => (
                        <TaskListRow
                          key={t.id}
                          task={t}
                          client={clientById(data.clients, t.client_id)}
                          ownerName={ownerLabel(t.owner_id)}
                          canEdit
                          onEdit={openDetail}
                        />
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        </div>
      )}

      <TaskDetailModal
        open={Boolean(selectedTask)}
        task={selectedTask}
        client={detailClient}
        canOpenAccount={detailCanOpenAccount}
        meetingMinute={detailMinute}
        ownerName={selectedTask ? ownerLabel(selectedTask.owner_id) : '—'}
        canEdit={selectedTask ? canEditTask(selectedTask) : false}
        completing={completing}
        onClose={() => setSelectedTask(null)}
        onEdit={openEditFromDetail}
        onDone={() => void handleDoneFromDetail()}
      />

      <TaskFormModal
        open={editOpen}
        task={editing}
        onClose={() => {
          setEditOpen(false);
          setEditing(null);
        }}
      />

      <Modal
        title="Export PDF"
        open={exportOpen}
        onClose={() => {
          if (!exporting) setExportOpen(false);
        }}
        compactHeader
      >
        <div className="tasks-export-modal">
          <p className="tasks-export-modal__hint">Choose the date range for the visit list PDF.</p>
          <div className="tasks-export-modal__dates">
            <label className="tasks-export-modal__date-field">
              <span className="field-label">From</span>
              <input
                type="date"
                className="field-input tasks-export-modal__date-input"
                value={exportFrom}
                onChange={(e) => setExportFrom(e.target.value)}
                disabled={exporting}
                aria-label="Export period start"
              />
            </label>
            <label className="tasks-export-modal__date-field">
              <span className="field-label">To</span>
              <input
                type="date"
                className="field-input tasks-export-modal__date-input"
                value={exportTo}
                onChange={(e) => setExportTo(e.target.value)}
                disabled={exporting}
                aria-label="Export period end"
              />
            </label>
          </div>
          <div className="tasks-export-modal__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={exporting}
              onClick={() => setExportOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={!exportFrom || !exportTo || exporting}
              onClick={() => void handleExportPdf()}
            >
              {exporting ? 'Generating…' : 'Export'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
